import { hideAllPoppers } from "floating-vue";
import { capitalize, type Component, computed, nextTick } from "vue";

import { backendOld } from "~/api";
import { getPropertyConfig } from "~/common/properties";
import createAndOpenAgent from "~/components/agent/createAndOpenAgent";
import { isAssistantTextMessage } from "~/components/chat/messageSectionUtils";
import ColorPicker from "~/components/dumb/ColorPicker.vue";
import type ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import DropdownToggleItem from "~/components/dumb/DropdownToggleItem.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import { copyAndNotify, notify } from "~/components/notifications";
import OptionTitleEditor from "~/components/options/OptionTitleEditor.vue";
import PagePropertiesEditorDartboardInfoSummary from "~/components/PagePropertiesEditorDartboardInfoSummary.vue";
import { getCopyItems } from "~/components/text/copyItems";
import { AGENT_CONFIG_MAP } from "~/constants/agents/configs";
import { REVIEWER_TENANT_DUIDS } from "~/constants/app";
import { USER_KIND_CUSTOM, USER_KIND_DART_AI, USER_KIND_USER } from "~/constants/userKinds";
import {
  AccountSettingsIcon,
  AdminIcon,
  AgentIcon,
  AttachmentFieldIcon,
  BrainstormIcon,
  ChangeIconIcon,
  ChangelogIcon,
  ChevronLeftDoubleIcon,
  ChildRelationshipIcon,
  DatesFieldIcon,
  DescriptionFieldIcon,
  DueDateFieldIcon,
  DuidFieldIcon,
  EditIcon,
  FolderIcon,
  FullscreenIcon,
  GitBranchIcon,
  LinkIcon,
  LogOutIcon,
  MailIcon,
  NotificationIcon,
  OpenExternalLinkIcon,
  OutdentIcon,
  PinIcon,
  PlanProjectIcon,
  PromptIcon,
  PublicIcon,
  RecommendationIcon,
  RecurrenceIcon,
  ReminderIcon,
  RenameIcon,
  RepeatIcon,
  ReplicateIcon,
  RestoreIcon,
  SettingsIcon,
  ShareIcon,
  ShowIcon,
  SpaceFieldIcon,
  SprintIcon,
  SprintRolloverIcon,
  StandupReportIcon,
  TrashIcon,
  UndoIcon,
  UnreadIcon,
  UpgradeRequiredIcon,
  UsersRemoveIcon,
} from "~/icons";
import {
  CommandId,
  DartboardKind,
  DropdownMenuItemKind,
  EditorMode,
  FolderKind,
  NotificationType,
  PageKind,
  Placement,
  SkillKind,
  SpaceKind,
  SprintMode,
  Subscription,
  TaskDetailMode,
  UserRole,
  UserStatus,
  ViewKind,
} from "~/shared/enums";
import type {
  Automation,
  Chat,
  Comment,
  Dartboard,
  Doc,
  DropdownMenuSection,
  Folder,
  Form,
  Option,
  Page,
  Property,
  SimpleDateRange,
  Skill,
  Task,
  TaskKind,
  User,
} from "~/shared/types";
import { useAppStore, useDataStore, useEnvironmentStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { useUpgrade } from "~/utils/billing";
import {
  getChatLink,
  getDocLink,
  getPageDisplayName,
  getPublicViewLink,
  getText,
  getUserPageLink,
  makeDartboardEmail,
  makeTaskEmail,
} from "~/utils/common";
import { getTitleOrNoTitle } from "~/utils/entityTitle";
import { getOrdersBetween } from "~/utils/orderManager";
import { isAgentDisabled, isAgentSetupRequired, isAgentUser } from "~/utils/user";
import { when } from "~/utils/wait";

import type { Actions } from ".";

const PAGE_KIND_TO_DELETE_DIALOG_DESCRIPTION = new Map([
  [
    PageKind.DARTBOARD,
    "Permanently deleting a dartboard will remove it for everyone in the workspace and move all its tasks to the trash. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.DASHBOARD,
    "Permanently deleting a dashboard will remove it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.DOC,
    "Permanently deleting a doc will remove it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.FOLDER,
    "Permanently deleting a folder will remove it and all of its docs for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.SPACE,
    "Permanently deleting a space will remove it and everything within it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.VIEW,
    "Permanently deleting a view will remove it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
]);
const PAGE_KINDS_WITHOUT_CONTEXT_MENUS = new Set([
  PageKind.AGENTS,
  PageKind.DASHBOARDS_ROOT,
  PageKind.HOME,
  PageKind.INBOX,
  PageKind.VIEWS_ROOT,
]);
const OPTION_DELETE_DIALOG_DESCRIPTION =
  "Permanently deleting an option will remove it from any tasks it is on. This can't be undone. Are you sure you want to proceed?";

const DELETE_AGENT_DIALOG_DESCRIPTION =
  "Permanently deleting an agent can't be undone. Are you sure you want to proceed?";

type ChatContextMenuOptions = {
  onDelete: (duid: string) => void;
  onMarkUnread?: () => void;
  startEditingTitle?: () => void | Promise<void>;
};

type TaskContextMenuOptions = {
  startEditingTitle?: () => void | Promise<void>;
};

let LinkToRelationshipsDropdown: Component;
let OptionEditDropdown: Component;
let DatePicker: Component;
let DocInfoSummary: Component;
let DropdownDeleteMenuItem: Component;
let ExportDropdown: Component;
let GenericSubdropdown: Component;
let TaskDropdown: Component;
let TaskUpdateDropdown: Component;

type ContextComponents = {
  DatePicker: Component;
  DocInfoSummary: Component;
  DropdownDeleteMenuItem: Component;
  ExportDropdown: Component;
  GenericSubdropdown: Component;
  LinkToRelationshipsDropdown: Component;
  OptionEditDropdown: Component;
  TaskDropdown: Component;
  TaskUpdateDropdown: Component;
};

/** Context menu actions */
export default (actions: Actions) => ({
  init({
    DatePicker: datePicker,
    DocInfoSummary: docInfoSummary,
    DropdownDeleteMenuItem: dropdownDeleteMenuItem,
    ExportDropdown: exportDropdown,
    GenericSubdropdown: genericSubdropdown,
    LinkToRelationshipsDropdown: linkToRelationshipsDropdown,
    OptionEditDropdown: optionEditDropdown,
    TaskDropdown: taskDropdown,
    TaskUpdateDropdown: taskUpdateDropdown,
  }: ContextComponents) {
    DatePicker = datePicker;
    DocInfoSummary = docInfoSummary;
    DropdownDeleteMenuItem = dropdownDeleteMenuItem;
    ExportDropdown = exportDropdown;
    GenericSubdropdown = genericSubdropdown;
    LinkToRelationshipsDropdown = linkToRelationshipsDropdown;
    OptionEditDropdown = optionEditDropdown;
    TaskDropdown = taskDropdown;
    TaskUpdateDropdown = taskUpdateDropdown;
  },
  automation(automation: Automation, dialog: InstanceType<typeof ConfirmationDialog> | null) {
    const environmentStore = useEnvironmentStore();

    return [
      {
        title: "Options",
        items: [
          {
            title: "Copy link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => {
              const url = environmentStore.getAutomationSettingsUrl(automation.duid);
              copyAndNotify("Link to automation settings", url);
            },
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Automation ID", automation.duid),
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownDeleteMenuItem,
            componentArgs: {
              dialog: dialog ?? undefined,
            },
          },
        ],
      },
    ];
  },
  comment(comment: Comment, emit: (e: string) => void) {
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const userStore = useUserStore();

    const isAuthor = comment.authorDuid === userStore.duid;
    const author = dataStore.getUserByDuid(comment.authorDuid);
    const canDelete = isAuthor || (author !== undefined && isAgentUser(author));

    const sections = [];

    if (isAuthor) {
      sections.push({
        title: "Edit",
        items: [
          {
            title: "Edit",
            kind: DropdownMenuItemKind.BUTTON,
            icon: EditIcon,
            onClick: () => emit("startEdit"),
          },
        ],
      });
    }

    sections.push({
      title: "Options",
      items: [
        {
          title: "Copy link",
          kind: DropdownMenuItemKind.BUTTON,
          icon: LinkIcon,
          onClick: () => {
            const text = getText(comment.text);
            const title = text.length > 50 ? `${text.slice(0, 50)}…` : text;
            const url = environmentStore.getCommentUrl(comment);
            copyAndNotify("Link to comment", url, `<a href="${url}">${getTitleOrNoTitle(title)}</a>`);
          },
        },
        ...getCopyItems("Comment", comment.text),
        {
          title: "Copy ID",
          kind: DropdownMenuItemKind.BUTTON,
          icon: DuidFieldIcon,
          onClick: () => copyAndNotify("Comment ID", comment.duid),
        },
      ],
    });

    if (canDelete) {
      sections.push({
        title: "Delete",
        items: [
          {
            title: "Delete",
            kind: DropdownMenuItemKind.BUTTON,
            icon: TrashIcon,
            onClick: () => emit("delete"),
          },
        ],
      });
    }

    return sections;
  },
  chat(
    chat: Chat,
    selectedAgent: User,
    { onDelete, onMarkUnread, startEditingTitle }: ChatContextMenuOptions
  ): DropdownMenuSection[] {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const router = dataStore.$router;

    const hasSentMessages = dataStore.getTextMessages(chat.duid).some((message) => !isAssistantTextMessage(message));

    const openDeleteConfirmationModal = () => {
      const currentChat = dataStore.getChatByDuidIfLoaded(chat.duid);
      if (!currentChat) {
        return;
      }

      appStore.setDeleteConfirmationModalOpen({
        title: `Delete ${currentChat.title}`,
        description: "Permanently deleting a chat can't be undone. Are you sure you want to proceed?",
        onDelete: () => onDelete(currentChat.duid),
      });
    };

    const markUnread = () => {
      dataStore.updateChat({ duid: chat.duid, read: false });
      onMarkUnread?.();
    };

    const copyLink = () => {
      const chatLink = getChatLink(chat);
      const link = chat.rootTaskDuid
        ? { name: "build_chat", params: { ...chatLink.params, taskDuid: chat.rootTaskDuid } }
        : chatLink;
      const url = window.location.href.includes(chat.duid)
        ? window.location.href
        : `${environmentStore.urlBase}${router.resolve(link).href}`;
      copyAndNotify("Link to chat", url, `<a href="${url}">${chat.title || "New chat"}</a>`);
    };

    return [
      {
        title: "Chat",
        items: [
          {
            title: "Pin",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownToggleItem,
            componentArgs: {
              title: "Pin",
              icon: PinIcon,
              value: chat.pinned,
              onUpdate: (value: boolean) => dataStore.updateChat({ duid: chat.duid, pinned: value }),
            },
            noFocus: true,
          },
          {
            title: "Rename",
            kind: DropdownMenuItemKind.BUTTON,
            icon: RenameIcon,
            hidden: !startEditingTitle,
            onClick: startEditingTitle,
          },
          {
            title: "Mark unread",
            kind: DropdownMenuItemKind.BUTTON,
            icon: UnreadIcon,
            hidden: !chat.read || !hasSentMessages,
            onClick: markUnread,
          },
        ],
      },
      {
        title: "Copy",
        items: [
          {
            title: "Copy link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: copyLink,
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Chat ID", chat.duid),
          },
        ],
      },
      {
        title: "Settings",
        items: [
          {
            title: "Agent settings",
            kind: DropdownMenuItemKind.INTERNAL_LINK,
            icon: SettingsIcon,
            navigate: {
              to: getUserPageLink(selectedAgent, "settings"),
            },
          },
        ],
      },
      {
        title: "Meta",
        items: [
          {
            title: "Delete",
            kind: DropdownMenuItemKind.BUTTON,
            icon: TrashIcon,
            onClick: openDeleteConfirmationModal,
          },
        ],
      },
    ];
  },
  doc(
    doc: Doc,
    options: {
      fullEdit?: boolean;
      startEditingTitle?: () => void;
      close?: (() => void) | undefined;
    } = {}
  ) {
    const { fullEdit, startEditingTitle, close } = options;
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const pageStore = usePageStore();
    const userStore = useUserStore();
    const router = dataStore.$router;

    return [
      {
        title: "Actions",
        items: [
          {
            title: "Open doc",
            kind: DropdownMenuItemKind.BUTTON,
            icon: FullscreenIcon,
            hidden: appStore.docOpenInFullscreen?.duid === doc.duid,
            onClick: async () => {
              await dataStore.getDocByDuid(doc.duid);
              await nextTick();
              router.push(getDocLink(doc));
            },
          },
        ],
      },
      {
        title: "Modify",
        items: [
          {
            title: "Rename",
            kind: DropdownMenuItemKind.BUTTON,
            hidden: !startEditingTitle,
            icon: RenameIcon,
            onClick: startEditingTitle,
          },
          {
            title: "Doc AI",
            kind: DropdownMenuItemKind.BUTTON,
            hidden:
              !pageStore.isMobile ||
              !appStore.docOpenInFullscreen ||
              !pageStore.isOnline ||
              !userStore.isRoleGte(UserRole.MEMBER),
            icon: RecommendationIcon,
            iconArgs: { class: "text-recommendation-base icon-sm" },
            onClick: () => appStore.doc?.openRecommendations(),
          },
          {
            title: "Link a task",
            kind: DropdownMenuItemKind.COMPONENT,
            component: TaskDropdown,
            componentArgs: {
              doc,
            },
          },
          {
            title: "Change folder",
            kind: DropdownMenuItemKind.COMPONENT,
            component: GenericSubdropdown,
            componentArgs: {
              title: "Change folder",
              icon: FolderIcon,
              sections: (() => {
                const currentFolder = dataStore.getFolderByDuid(doc.folderDuid);
                const currentSpaceDuid = currentFolder?.spaceDuid;
                const sortedSpaces = [...dataStore.spaceList].sort((a, b) => {
                  if (a.duid === currentSpaceDuid) {
                    return -1;
                  }
                  if (b.duid === currentSpaceDuid) {
                    return 1;
                  }
                  return 0;
                });
                return sortedSpaces.map((space) => ({
                  title: space.title,
                  showTitle: true,
                  items: dataStore.getFoldersBySpaceDuidOrdered(space.duid).map((folder) => ({
                    title: getPageDisplayName(folder, dataStore.getSpaceByDuid),
                    kind: DropdownMenuItemKind.BUTTON,
                    disabled: folder.duid === doc.folderDuid,
                    icon: PageIcon,
                    iconArgs: { page: folder },
                    onClick: () =>
                      dataStore.updateDocs([
                        {
                          duid: doc.duid,
                          folderDuid: folder.duid,
                          order: getOrdersBetween(undefined, folder.topDocOrder)[0],
                        },
                      ]),
                  })),
                }));
              })(),
            },
          },
          {
            title: "Add attachment",
            kind: DropdownMenuItemKind.BUTTON,
            hidden: !fullEdit || !userStore.hasAccessToImportExport(),
            icon: AttachmentFieldIcon,
            onClick: async () => {
              appStore.doc?.openFilePicker();
              close?.();
            },
          },
        ],
      },
      {
        title: "Copy",
        items: [
          {
            title: "Copy link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => actions.doc.copyLinks([doc]),
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Doc ID", doc.duid),
          },
        ],
      },
      {
        title: "Meta",
        items: [
          {
            title: "Export as",
            kind: DropdownMenuItemKind.COMPONENT,
            component: ExportDropdown,
            componentArgs: {
              doc,
            },
          },
          {
            title: "Replicate",
            kind: DropdownMenuItemKind.BUTTON,
            icon: ReplicateIcon,
            onClick: async () => {
              const siblings = dataStore.getLoadedDocsByFolderDuidOrdered(doc.folderDuid);
              const prevSibling = siblings[siblings.findIndex((e) => e.duid === doc.duid) - 1];
              const order = getOrdersBetween(prevSibling?.order, doc.order)[0];

              const replicate = await dataStore.replicateDoc(doc, order);
              if (!replicate) {
                return;
              }
              router.push(getDocLink(replicate));
            },
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownDeleteMenuItem,
            componentArgs: {
              title: `Delete ${getPageDisplayName(doc, dataStore.getSpaceByDuid)}`,
              description:
                "Permanently deleting a doc will remove it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
              onDelete: () => dataStore.trashDoc(doc),
            },
          },
        ],
      },
      {
        title: "Doc Info",
        showTitle: false,
        items: [
          {
            title: "",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DocInfoSummary,
            componentArgs: {
              doc,
            },
            noFocus: true,
          },
        ],
      },
    ];
  },
  form(form: Form, dialog: InstanceType<typeof ConfirmationDialog> | null) {
    const environmentStore = useEnvironmentStore();

    return [
      {
        title: "Options",
        items: [
          {
            title: "Open preview",
            kind: DropdownMenuItemKind.EXTERNAL_LINK,
            icon: OpenExternalLinkIcon,
            navigate: {
              to: environmentStore.getFormUrl(form),
              newTab: true,
            },
          },
          {
            title: "Copy public link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => {
              const formUrl = environmentStore.getFormUrl(form);
              copyAndNotify(
                "Public link to form",
                formUrl,
                `<a href="${formUrl}">${getTitleOrNoTitle(form.title)}</a>`
              );
            },
          },
          {
            title: "Copy settings link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: SettingsIcon,
            onClick: () => {
              const fullUrl = environmentStore.getFormSettingsUrl(form.duid);
              copyAndNotify("Link to form settings", fullUrl);
            },
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Form ID", form.duid),
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownDeleteMenuItem,
            componentArgs: {
              dialog: dialog ?? undefined,
            },
          },
        ],
      },
    ];
  },
  option(
    option: Option,
    options: {
      showTitleEditor?: boolean;
      property?: Property;
      addSubitem?: () => void;
      onRename?: (newTitle: string) => void;
      onChangeColor?: (newColorHex: string) => void;
      onClose?: () => void;
    } = {}
  ) {
    const { showTitleEditor, property, addSubitem, onRename, onChangeColor, onClose } = options;
    const dataStore = useDataStore();
    const userStore = useUserStore();

    const propertyNorm = property ?? dataStore.getPropertyByDuid(option.propertyDuid);

    return [
      {
        title: "Title",
        items: [
          {
            title: onRename ? "Add" : "Rename",
            kind: DropdownMenuItemKind.COMPONENT,
            noFocus: true,
            hidden: showTitleEditor,
            component: OptionTitleEditor,
            componentArgs: {
              option,
              property: propertyNorm,
              adding: onRename !== undefined,
              onRename: (title: string) => {
                if (onRename) {
                  onRename(title);
                  return;
                }
                dataStore.updateOption({ duid: option.duid, title });
              },
              onClose,
            },
          },
        ],
      },
      {
        title: "Modify",
        items: [
          {
            title: "Change color",
            kind: DropdownMenuItemKind.COMPONENT,
            noFocus: true,
            component: ColorPicker,
            componentArgs: {
              value: option.colorHex,
              onSelect: (colorHex: string) => {
                if (onChangeColor) {
                  onChangeColor(colorHex);
                  return;
                }
                dataStore.updateOption({ duid: option.duid, colorHex });
              },
            },
          },
        ],
      },
      {
        title: "Meta",
        items: [
          {
            title: "Add sub-item",
            kind: addSubitem ? DropdownMenuItemKind.BUTTON : DropdownMenuItemKind.COMPONENT,
            hidden: !!onRename,
            icon: ChildRelationshipIcon,
            onClick: addSubitem,
            component: addSubitem ? undefined : OptionEditDropdown,
            componentArgs: addSubitem
              ? undefined
              : {
                  option,
                  isSubitemDropdownMenu: true,
                },
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            hidden: onRename !== undefined,
            onClick: () => copyAndNotify("Option ID", option.duid),
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            hidden: !userStore.isRoleGte(UserRole.ADMIN) || onRename !== undefined,
            component: DropdownDeleteMenuItem,
            componentArgs: {
              title: `Delete ${option.title}`,
              description: OPTION_DELETE_DIALOG_DESCRIPTION,
              onDelete: () => dataStore.deleteOption(option),
            },
          },
        ],
      },
    ];
  },
  page(page: Page, showDescriptionOption: boolean, emit: (e: string) => void) {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const pageStore = usePageStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();
    const showDartboardAiSubmenu =
      showDescriptionOption &&
      page.pageKind === PageKind.DARTBOARD &&
      pageStore.isOnline &&
      !REVIEWER_TENANT_DUIDS.has(tenantStore.duid) &&
      userStore.hasAccessToAiChat();

    const hasEditAccess = userStore.hasAccessToPageEditing();
    const isWorkspaceOrPersonal = page.kind === SpaceKind.WORKSPACE || page.kind === SpaceKind.PERSONAL;

    if (PAGE_KINDS_WITHOUT_CONTEXT_MENUS.has(page.pageKind)) {
      return [];
    }

    const renameItem = {
      title: "Rename",
      kind: DropdownMenuItemKind.BUTTON,
      icon: RenameIcon,
      hidden: !hasEditAccess && page.pageKind !== PageKind.DOC,
      onClick: () => emit("startEditingTitle"),
    };

    const changeIconItem = {
      title: "Change icon",
      kind: DropdownMenuItemKind.BUTTON,
      icon: ChangeIconIcon,
      hidden: !hasEditAccess,
      onClick: () => emit("startEditingIcon"),
    };

    const replicatePageItem = computed(() => ({
      title: "Replicate",
      kind: DropdownMenuItemKind.BUTTON,
      icon: ReplicateIcon,
      hidden: !hasEditAccess && page.pageKind !== PageKind.DOC,
      dataTestid: "dropdown-replicate-page",
      onClick: () => dataStore.replicatePage(page),
    }));

    const manageSharingItem = {
      title: "Manage sharing",
      kind: DropdownMenuItemKind.BUTTON,
      icon: ShareIcon,
      hidden: !hasEditAccess || !(page.pageKind === PageKind.VIEW || page.pageKind === PageKind.DASHBOARD),
      onClick: () => {
        if (!page || !(page.pageKind === PageKind.VIEW || page.pageKind === PageKind.DASHBOARD)) {
          return;
        }
        appStore.setPageInPermissionsModal(page);
      },
    };

    const editDescriptionItem = {
      title: "Edit description",
      kind: DropdownMenuItemKind.BUTTON,
      icon: DescriptionFieldIcon,
      hidden: !hasEditAccess || !showDescriptionOption || pageStore.isMobile,
      onClick: () => emit("startEditingDescription"),
    };

    const pageName = getPageDisplayName(page, dataStore.getSpaceByDuid);
    const pageUrl = environmentStore.getPageUrl(page);
    const copyLinkItem = {
      title: "Copy link",
      kind: DropdownMenuItemKind.BUTTON,
      icon: LinkIcon,
      onClick: () =>
        copyAndNotify(`Link to ${page.pageKind}`, pageUrl, `<a href="${pageUrl}">${getTitleOrNoTitle(pageName)}</a>`),
    };

    const copyIdItem = {
      title: "Copy ID",
      kind: DropdownMenuItemKind.BUTTON,
      icon: DuidFieldIcon,
      onClick: () => copyAndNotify(`${capitalize(page.pageKind)} ID`, page.duid),
    };

    const exportItem = {
      title: "Export as",
      kind: DropdownMenuItemKind.COMPONENT,
      component: ExportDropdown,
      componentArgs: {
        page,
      },
    };

    const isDefaultFolder = computed(() => page.pageKind === PageKind.FOLDER && page.kind === FolderKind.DEFAULT);
    const isOnlyDartboardInGeneral = computed(
      () =>
        page.pageKind === PageKind.DARTBOARD &&
        page.spaceDuid === dataStore.workspaceSpace?.duid &&
        dataStore.getDartboardsBySpaceDuidOrdered(page.spaceDuid).length === 1
    );

    const isWorkspaceSpace = computed(() => page.pageKind === PageKind.SPACE && page.kind === SpaceKind.WORKSPACE);
    const isPersonalSpace = computed(() => page.pageKind === PageKind.SPACE && page.kind === SpaceKind.PERSONAL);

    const deleteItem = {
      title: "Delete",
      kind: DropdownMenuItemKind.COMPONENT,
      component: DropdownDeleteMenuItem,
      hidden: !hasEditAccess && page.pageKind !== PageKind.DOC,
      disabled:
        isDefaultFolder.value || isOnlyDartboardInGeneral.value || isWorkspaceSpace.value || isPersonalSpace.value,
      disabledReason: isDefaultFolder.value
        ? "This is the default folder and can't be deleted"
        : isOnlyDartboardInGeneral.value
          ? "This is the only dartboard in General and can't be deleted"
          : isWorkspaceSpace.value
            ? "This is the General space and can't be deleted"
            : isPersonalSpace.value
              ? "This is the Personal space and can't be deleted"
              : undefined,
      componentArgs: {
        title: `Delete ${pageName}`,
        description: PAGE_KIND_TO_DELETE_DIALOG_DESCRIPTION.get(page.pageKind),
        onDelete: () => dataStore.deletePage(page),
      },
    };

    const changeSpaceItem = {
      title: "Change space",
      kind: DropdownMenuItemKind.COMPONENT,
      component: GenericSubdropdown,
      hidden: !hasEditAccess || isDefaultFolder.value || isOnlyDartboardInGeneral.value,
      componentArgs: {
        title: "Change space",
        icon: SpaceFieldIcon,
        sections: [
          {
            title: "Change space",
            items: dataStore.spaceList.map((space) => ({
              title: space.title,
              kind: DropdownMenuItemKind.BUTTON,
              disabled: space.duid === (page as Dartboard | Folder).spaceDuid,
              icon: PageIcon,
              iconArgs: { page: space },
              onClick: () => {
                appStore.movePageToSpace(page as Dartboard | Folder, space.duid);
                hideAllPoppers();
                appStore.contextMenu = null;
              },
            })),
          },
        ],
      },
    };

    switch (page.pageKind) {
      case PageKind.DARTBOARD: {
        const runSkillByKind = async (skillKind: SkillKind) => {
          if (!tenantStore.isSubscriptionGte(Subscription.APP_SUMO)) {
            appStore.setUpgradeConfirmationModalOpen({
              featureAction: "use AI skills",
              subscription: Subscription.PREMIUM,
            });
            return;
          }

          const skill = dataStore.skillList({ includeHidden: true }).find((item) => item.kind === skillKind);
          if (!skill) {
            return;
          }

          if (!userStore.getAiChatPanelOpen()) {
            userStore.setAiChatPanelOpen(true);
            await nextTick();
          }

          await appStore.chatPanel?.sendContentInNewChat(skill);
        };

        const dartboardAiItem = {
          title: "Dartboard AI",
          kind: DropdownMenuItemKind.COMPONENT,
          hidden: !showDartboardAiSubmenu,
          component: GenericSubdropdown,
          componentArgs: {
            title: "Dartboard AI",
            icon: RecommendationIcon,
            iconArgs: { class: "text-recommendation-base icon-sm" },
            widthPixels: 208,
            sections: [
              {
                title: "Dartboard AI",
                items: [
                  {
                    title: "Brainstorm new tasks",
                    kind: DropdownMenuItemKind.BUTTON,
                    icon: BrainstormIcon,
                    onClick: () => runSkillByKind(SkillKind.BRAINSTORM_NEW_TASKS),
                  },
                  {
                    title: "Plan this project",
                    kind: DropdownMenuItemKind.BUTTON,
                    icon: PlanProjectIcon,
                    onClick: () => runSkillByKind(SkillKind.PLAN_PROJECT),
                  },
                  {
                    title: "Standup report",
                    kind: DropdownMenuItemKind.BUTTON,
                    icon: StandupReportIcon,
                    onClick: () => runSkillByKind(SkillKind.STANDUP_REPORT),
                  },
                  {
                    title: "Write a changelog",
                    kind: DropdownMenuItemKind.BUTTON,
                    icon: ChangelogIcon,
                    onClick: () => runSkillByKind(SkillKind.WRITE_CHANGELOG),
                  },
                ],
              },
            ],
          },
        };

        const infoSection = {
          title: "Info",
          items: [
            {
              title: "Info",
              kind: DropdownMenuItemKind.COMPONENT,
              component: PagePropertiesEditorDartboardInfoSummary,
              componentArgs: {
                dartboard: page,
              },
              noFocus: true,
            },
          ],
        };

        const resetRolloverItemMaybe =
          dataStore
            .getDartboardsByKindOrdered(DartboardKind.FINISHED)
            .find(({ spaceDuid }) => spaceDuid === page.spaceDuid)?.duid === page.duid
            ? [
                {
                  title: "Reset to Active",
                  kind: DropdownMenuItemKind.BUTTON,
                  icon: UndoIcon,
                  onClick: () => emit("resetToActive"),
                },
              ]
            : [];

        const rolloverItem = {
          title: `Start ${page.kind === DartboardKind.NEXT ? "this" : "next"} sprint`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: SprintRolloverIcon,
          onClick: () => emit("startNextSprint"),
        };

        const configureSprintsItem = {
          title: "Sprint settings",
          kind: DropdownMenuItemKind.BUTTON,
          icon: SprintIcon,
          hidden: !hasEditAccess,
          onClick: () => emit("startEditingSpace"),
        };

        const planFinishItem = {
          title: "Plan finish",
          kind: DropdownMenuItemKind.COMPONENT,
          component: DatePicker,
          componentArgs: {
            value: page.plannedFinishAt,
            editorMode: EditorMode.CONTEXT_MENU,
            titleOverride: "Plan finish",
            iconOverride: DueDateFieldIcon,
            hideDestructiveActions: true,
            placement: Placement.RIGHT_TOP,
            onSelect: (date: string | null) => dataStore.updateDartboard({ duid: page.duid, plannedFinishAt: date }),
          },
          icon: DueDateFieldIcon,
        };

        const activeDartboard = dataStore
          .getDartboardsBySpaceDuidOrdered(page.spaceDuid)
          .find((e) => e.kind === DartboardKind.ACTIVE);

        const planStartAndFinishItem = {
          title: "Plan start and finish",
          kind: DropdownMenuItemKind.COMPONENT,
          component: DatePicker,
          componentArgs: {
            value: { start: activeDartboard?.plannedFinishAt ?? null, end: page.plannedFinishAt },
            editorMode: EditorMode.CONTEXT_MENU,
            titleOverride: "Plan start and finish",
            iconOverride: DatesFieldIcon,
            hideDestructiveActions: true,
            placement: Placement.RIGHT_TOP,
            onSelectRange: ({ start, end }: SimpleDateRange) => {
              if (!activeDartboard) {
                return;
              }
              dataStore.updateDartboard({
                duid: activeDartboard.duid,
                plannedFinishAt: start ?? null,
              });
              dataStore.updateDartboard({ duid: page.duid, plannedFinishAt: end ?? null });
            },
          },
          icon: DatesFieldIcon,
        };

        const space = dataStore.getSpaceByDuid(page.spaceDuid);
        const copyDartboardEmailItem = {
          title: "Copy dartboard email",
          kind: DropdownMenuItemKind.BUTTON,
          icon: MailIcon,
          hidden: !tenantStore.emailIntegrationEnabled,
          onClick: () => copyAndNotify(`Email for ${pageName}`, makeDartboardEmail(page, environmentStore.isProd)),
        };

        switch (page.kind) {
          case DartboardKind.ACTIVE:
          case DartboardKind.NEXT: {
            return [
              infoSection,
              { title: "Modify", items: [editDescriptionItem, dartboardAiItem] },
              {
                title: "Manage",
                items: [
                  rolloverItem,
                  configureSprintsItem,
                  ...(space?.rolloverRecurrence !== null
                    ? []
                    : page.kind === DartboardKind.ACTIVE
                      ? [planFinishItem]
                      : [planStartAndFinishItem]),
                ],
              },
              { title: "Copy", items: [copyLinkItem, copyDartboardEmailItem, copyIdItem] },
              { title: "Meta", items: [exportItem] },
            ];
          }
          case DartboardKind.BACKLOG: {
            return [
              { title: "Modify", items: [editDescriptionItem, dartboardAiItem] },
              { title: "Manage", items: [configureSprintsItem] },
              { title: "Copy", items: [copyLinkItem, copyDartboardEmailItem, copyIdItem] },
              { title: "Meta", items: [exportItem] },
            ];
          }
          case DartboardKind.FINISHED: {
            return [
              infoSection,
              { title: "Modify", items: [editDescriptionItem, dartboardAiItem] },
              { title: "Manage", items: [...resetRolloverItemMaybe, configureSprintsItem] },
              { title: "Copy", items: [copyLinkItem, copyIdItem] },
              { title: "Meta", items: [exportItem, deleteItem] },
            ];
          }
          case DartboardKind.CUSTOM: {
            return [
              {
                title: "Modify",
                items: [renameItem, changeIconItem, editDescriptionItem, changeSpaceItem, dartboardAiItem],
              },
              { title: "Copy", items: [copyLinkItem, copyDartboardEmailItem, copyIdItem] },
              { title: "Meta", items: [replicatePageItem.value, exportItem, deleteItem] },
            ];
          }
          default: {
            throw new Error("Unknown dartboard kind");
          }
        }
      }
      case PageKind.DASHBOARD: {
        const isFavorited = page.favoritedByUserDuids.includes(userStore.duid);
        const toggleFavoriteItem = {
          title: "Pin",
          kind: DropdownMenuItemKind.COMPONENT,
          component: DropdownToggleItem,
          componentArgs: {
            title: "Pin",
            icon: PinIcon,
            value: isFavorited,
            onUpdate: (value: boolean) => {
              if (!page) {
                return;
              }
              dataStore.updateDashboardFavorite(page.duid, value);
            },
          },
          noFocus: true,
        };

        return [
          { title: "Pin", items: [toggleFavoriteItem] },
          {
            title: "Modify",
            items: [renameItem, changeIconItem, editDescriptionItem, manageSharingItem],
          },
          { title: "Copy", items: [copyLinkItem, copyIdItem] },
          { title: "Meta", items: [replicatePageItem.value, deleteItem] },
        ];
      }
      case PageKind.DOC: {
        return actions.context.doc(page, {
          startEditingTitle: () => emit("startEditingTitle"),
        });
      }
      case PageKind.FOLDER: {
        return [
          { title: "Modify", items: [renameItem, changeIconItem, editDescriptionItem, changeSpaceItem] },
          { title: "Copy", items: [copyLinkItem, copyIdItem] },
          { title: "Meta", items: [replicatePageItem.value, deleteItem] },
        ];
      }
      case PageKind.SPACE: {
        return [
          {
            title: "Manage",
            items: [
              {
                title: "Start next sprint",
                kind: DropdownMenuItemKind.BUTTON,
                icon: SprintRolloverIcon,
                hidden: page.sprintMode === SprintMode.NONE,
                onClick: () => emit("startNextSprint"),
              },
              {
                title: "Rename",
                kind: DropdownMenuItemKind.BUTTON,
                icon: RenameIcon,
                hidden: !hasEditAccess || appStore.settingsModalOpen,
                onClick: () => emit("renameSpace"),
              },
              {
                title: "Space settings",
                kind: DropdownMenuItemKind.BUTTON,
                icon: SettingsIcon,
                dataTestid: "configure-space",
                hidden: !hasEditAccess || appStore.settingsModalOpen,
                onClick: () => emit("startEditingSpace"),
              },
              {
                title: "Manage sharing",
                kind: DropdownMenuItemKind.BUTTON,
                icon: ShareIcon,
                hidden: !hasEditAccess || appStore.settingsModalOpen || isWorkspaceOrPersonal,
                onClick: () => emit("manageSharing"),
              },
            ],
          },
          {
            title: "Copy",
            items: [
              {
                title: "Copy settings link",
                kind: DropdownMenuItemKind.BUTTON,
                icon: LinkIcon,
                onClick: () => {
                  const fullUrl = environmentStore.getSpaceSettingsUrl(page.duid, "basics");
                  copyAndNotify("Link to space settings", fullUrl);
                },
              },
              copyIdItem,
            ],
          },
          {
            title: "Meta",
            items: [replicatePageItem.value, deleteItem],
          },
        ];
      }
      case PageKind.VIEW: {
        const isFavorited = page.favoritedByUserDuids.includes(userStore.duid);
        const toggleFavoriteItem = {
          title: "Pin",
          kind: DropdownMenuItemKind.COMPONENT,
          component: DropdownToggleItem,
          componentArgs: {
            title: "Pin",
            icon: PinIcon,
            value: isFavorited,
            onUpdate: (value: boolean) => {
              if (!page) {
                return;
              }
              dataStore.updateViewFavorite(page.duid, value);
            },
          },
          noFocus: true,
        };

        if (page.kind === ViewKind.MY_TASKS) {
          return [
            { title: "Pin", items: [toggleFavoriteItem] },
            { title: "Copy", items: [copyLinkItem, copyIdItem] },
            { title: "Meta", items: [replicatePageItem.value, exportItem] },
          ];
        }

        const copyLinkItems = page.public
          ? [
              {
                title: "Copy public link",
                kind: DropdownMenuItemKind.BUTTON,
                icon: PublicIcon,
                onClick: () => {
                  const publicUrl = getPublicViewLink(page);
                  copyAndNotify(
                    "Public link to view",
                    publicUrl,
                    `<a href="${publicUrl}">${getTitleOrNoTitle(pageName)}</a>`
                  );
                },
              },
              { ...copyLinkItem, title: "Copy internal link" },
            ]
          : [copyLinkItem];
        return [
          { title: "Pin", items: [toggleFavoriteItem] },
          {
            title: "Modify",
            items: [renameItem, changeIconItem, editDescriptionItem, manageSharingItem],
          },
          { title: "Copy", items: [...copyLinkItems, copyIdItem] },
          { title: "Meta", items: [replicatePageItem.value, exportItem, deleteItem] },
        ];
      }
      default: {
        throw new Error(`Unknown page kind: ${page.pageKind}`);
      }
    }
  },
  property(property: Property, dialog: InstanceType<typeof ConfirmationDialog> | null) {
    const propertyConfig = getPropertyConfig(property.kind);
    const tenantStore = useTenantStore();
    const pageStore = usePageStore();
    const environmentStore = useEnvironmentStore();

    const items = [];

    if (tenantStore.isDart && !pageStore.adminHidden) {
      items.push({
        title: "Copy link by type",
        kind: DropdownMenuItemKind.BUTTON,
        icon: AdminIcon,
        onClick: () => {
          const fullUrl = environmentStore.getPropertySettingsByKindUrl(property.kind);
          copyAndNotify("Link to property by type", fullUrl);
        },
      });
    }

    items.push(
      {
        title: "Copy settings link",
        kind: DropdownMenuItemKind.BUTTON,
        icon: LinkIcon,
        onClick: () => {
          const fullUrl = environmentStore.getPropertySettingsUrl(property.duid);
          copyAndNotify("Link to property settings", fullUrl);
        },
      },
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: DuidFieldIcon,
        onClick: () => copyAndNotify("Property ID", property.duid),
      },
      {
        title: "Delete",
        kind: DropdownMenuItemKind.COMPONENT,
        component: DropdownDeleteMenuItem,
        disabled: propertyConfig.isDefault,
        disabledReason: propertyConfig.isDefault ? "This is a default property and can't be deleted" : undefined,
        componentArgs: {
          dialog: dialog ?? undefined,
        },
      }
    );

    return [
      {
        title: "Options",
        items,
      },
    ];
  },
  skill(skill: Skill, dialog: InstanceType<typeof ConfirmationDialog> | null) {
    const environmentStore = useEnvironmentStore();
    const pageStore = usePageStore();
    const tenantStore = useTenantStore();

    const items = [];

    if (tenantStore.isDart && !pageStore.adminHidden) {
      items.push({
        title: "Copy link by type",
        kind: DropdownMenuItemKind.BUTTON,
        icon: AdminIcon,
        onClick: () => {
          const fullUrl = environmentStore.getSkillSettingsByKindUrl(skill.kind);
          copyAndNotify("Link to skill by type", fullUrl);
        },
      });
    }

    items.push(
      {
        title: "Copy settings link",
        kind: DropdownMenuItemKind.BUTTON,
        icon: LinkIcon,
        onClick: () => {
          const fullUrl = environmentStore.getSkillSettingsUrl(skill.duid);
          copyAndNotify("Link to skill settings", fullUrl);
        },
      },
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: DuidFieldIcon,
        onClick: () => copyAndNotify("Skill ID", skill.duid),
      },
      {
        title: "Delete",
        kind: DropdownMenuItemKind.COMPONENT,
        component: DropdownDeleteMenuItem,
        disabled: skill.kind !== SkillKind.CUSTOM,
        disabledReason: skill.kind !== SkillKind.CUSTOM ? "This is a default skill and can't be deleted" : undefined,
        componentArgs: {
          dialog: dialog ?? undefined,
        },
      }
    );

    return [
      {
        title: "Options",
        items,
      },
    ];
  },
  task(
    targetTask: Task,
    editorMode: EditorMode,
    tasks?: Task[],
    shouldSelectTask = true,
    close?: () => void,
    options: TaskContextMenuOptions = {}
  ): DropdownMenuSection[] {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const pageStore = usePageStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    // Setup to get selected tasks
    const visualization = appStore.getActiveVisualization();

    const isListMode = editorMode === EditorMode.LIST;
    const isBoardMode = editorMode === EditorMode.BOARD;
    const isListMiniTcmMode = editorMode === EditorMode.LIST_MINI_TCM;
    const isListMiniNonRoadmapMode = editorMode === EditorMode.LIST_MINI || isListMiniTcmMode;
    const subtasksVisualization = isListMiniNonRoadmapMode ? appStore.getSubtasksListVisualization() : null;
    const subtasksSelectedRows = subtasksVisualization?.getSelectedRows() ?? [];
    const rowIsSelected = isListMiniNonRoadmapMode
      ? subtasksSelectedRows.some((row) => row.data?.duid === targetTask.duid)
      : appStore.selectedTaskDuids.has(targetTask.duid);

    if (shouldSelectTask) {
      if (isListMiniNonRoadmapMode && subtasksVisualization) {
        const hasMultipleSelected = subtasksSelectedRows.length > 1;
        if (rowIsSelected && hasMultipleSelected) {
          subtasksVisualization.scrollTo(targetTask.duid);
        } else {
          subtasksVisualization.selectAndScrollTo(targetTask.duid, {
            deselectOthers: !rowIsSelected,
          });
        }
      } else {
        actions.visualization.selectRowByIdAndScroll(targetTask.duid, !rowIsSelected);
      }
    }

    const contextTaskDuids =
      isListMiniNonRoadmapMode && subtasksSelectedRows.length > 0
        ? subtasksSelectedRows.map((row) => row.data?.duid).filter((taskDuid): taskDuid is string => !!taskDuid)
        : tasks
          ? tasks.map((e) => e.duid)
          : visualization.getSelected().map((e) => e.duid);

    const selectedTasks = dataStore.getTasksByDuidsIfLoaded(contextTaskDuids, {
      includeTrashed: true,
      includeDraft: true,
    });

    const selectedTaskDuids = selectedTasks.map((e) => e.duid);

    const isMultipleTasks = selectedTasks.length > 1;
    const plural = isMultipleTasks ? "s" : "";

    const userIsSubscriber = selectedTasks.every((e) => e.subscriberDuids.includes(userStore.duid));
    const isTrash = selectedTasks.every((e) => e.inTrash);

    const navigateToTask = async () => {
      actions.visualization.navigateToTask(targetTask.duid);
      await when(computed(() => appStore.selectedTaskDuids.has(targetTask.duid)));
    };

    // Dropdown menu sections
    const openTaskDetailSection: DropdownMenuSection = {
      title: "Actions",
      items: [
        {
          title: `Open task${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon:
            pageStore.isMobile || userStore.taskDetailMode !== TaskDetailMode.RIGHTBAR
              ? FullscreenIcon
              : ChevronLeftDoubleIcon,
          commandId: CommandId.OPEN_TASK,
          hidden: editorMode === EditorMode.DETAIL && appStore.taskOpenInDetail?.duid === targetTask.duid,
          disabled: isMultipleTasks || appStore.taskOpenInDetail?.duid === targetTask.duid,
          onClick: () => {
            if (isListMiniNonRoadmapMode) {
              actions.visualization.navigateToTask(targetTask.duid);
              return;
            }
            if (!rowIsSelected) {
              actions.visualization.navigateToTask(targetTask.duid);
              return;
            }
            appStore.openTaskDetailForTask(targetTask.duid);
          },
        },
        {
          title: `Convert to normal task${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: OutdentIcon,
          hidden: !isListMiniNonRoadmapMode,
          onClick: () => selectedTasks.forEach((task) => actions.visualization.convertToNormalTask(task)),
        },
      ],
    };

    const copySection: DropdownMenuSection = {
      title: "Copy",
      items: [
        {
          title: `Copy link${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: LinkIcon,
          commandId: CommandId.COPY_LINK,
          onClick: async () => actions.visualization.copyTaskLinks(selectedTasks),
        },
        {
          title: `Copy branch name${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: GitBranchIcon,
          hidden: !tenantStore.githubIntegrationEnabled,
          commandId: CommandId.COPY_BRANCH,
          onClick: async () => actions.visualization.copyTaskBranchNames(selectedTasks),
        },
        {
          title: "Copy task email",
          kind: DropdownMenuItemKind.BUTTON,
          icon: MailIcon,
          hidden: !tenantStore.emailIntegrationEnabled || isMultipleTasks,
          onClick: () => {
            const taskEmail = makeTaskEmail(targetTask.duid, environmentStore.isProd);
            copyAndNotify("Task email", taskEmail);
          },
        },
        {
          title: `Copy ID${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: DuidFieldIcon,
          onClick: () =>
            copyAndNotify(`Task ID${selectedTaskDuids.length > 1 ? "s" : ""}`, selectedTaskDuids.join("\n")),
        },
        {
          title: `Copy task${plural} as prompt${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: PromptIcon,
          commandId: CommandId.COPY_TASK_AS_PROMPT,
          onClick: async () => actions.visualization.copyTaskAsPrompt(selectedTasks),
        },
      ],
    };

    const deleteItem = {
      title: "Delete",
      kind: DropdownMenuItemKind.BUTTON,
      icon: TrashIcon,
      commandId: CommandId.DELETE_TASK,
      dataTestid: "dropdown-delete-task",
      onClick: async () => actions.visualization.trashTasks(selectedTasks),
    };

    if (isListMiniTcmMode) {
      const draftSubtasks = selectedTasks.filter((task) => task.drafterDuid !== null);
      const realSubtasks = selectedTasks.filter((task) => task.drafterDuid === null);

      const items = [];
      if (draftSubtasks.length > 0) {
        items.push({
          title: `Discard draft${draftSubtasks.length > 1 ? "s" : ""}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: TrashIcon,
          onClick: async () => dataStore.deleteTasks(draftSubtasks),
        });
      }
      if (realSubtasks.length > 0) {
        items.push({
          title: `Convert to normal task${realSubtasks.length > 1 ? "s" : ""}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: OutdentIcon,
          onClick: () => realSubtasks.forEach((task) => actions.visualization.convertToNormalTask(task)),
        });
      }

      return [
        {
          title: "Meta",
          items,
        },
      ];
    }

    if (isTrash) {
      return [
        openTaskDetailSection,
        copySection,
        {
          title: "Actions",
          items: [
            {
              title: "Restore",
              kind: DropdownMenuItemKind.BUTTON,
              icon: RestoreIcon,
              onClick: async () => actions.visualization.restoreTasks(),
            },
            {
              title: "Delete permanently",
              kind: DropdownMenuItemKind.BUTTON,
              commandId: CommandId.DELETE_TASK,
              icon: TrashIcon,
              onClick: async () => actions.visualization.deleteTasks(),
            },
          ],
        },
      ];
    }

    return [
      openTaskDetailSection,
      {
        title: "Modify",
        items: [
          {
            title: "Rename",
            kind: DropdownMenuItemKind.BUTTON,
            hidden: !options.startEditingTitle || isMultipleTasks,
            icon: RenameIcon,
            onClick: options.startEditingTitle,
          },
          {
            title: "Change task",
            kind: DropdownMenuItemKind.COMPONENT,
            component: TaskUpdateDropdown,
            componentArgs: {
              targetTask,
              selectedTasks,
            },
          },
          {
            title: "Connect a task, doc, and more",
            kind: DropdownMenuItemKind.COMPONENT,
            component: LinkToRelationshipsDropdown,
            componentArgs: {
              targetTask,
              selectedTasks,
            },
          },
          {
            title: "Add subtask",
            kind: DropdownMenuItemKind.BUTTON,
            icon: ChildRelationshipIcon,
            hidden: appStore.currentPage?.kind === DartboardKind.FINISHED,
            disabled: isMultipleTasks,
            commandId: CommandId.CREATE_SUBTASKS,
            onClick: async () => {
              if (!(isListMode || isBoardMode || isListMiniNonRoadmapMode) && !rowIsSelected) {
                await navigateToTask();
                actions.visualization.createSubtask(targetTask);
                return;
              }
              // Wait for the context menu to close
              // oxlint-disable-next-line dart/no-set-timeout
              setTimeout(() => {
                actions.visualization.createSubtask();
              }, 100);
            },
          },
          {
            title: "Add attachment",
            disabled: isMultipleTasks,
            hidden: !userStore.hasAccessToImportExport(),
            kind: DropdownMenuItemKind.BUTTON,
            icon: AttachmentFieldIcon,
            commandId: CommandId.ADD_ATTACHMENT,
            onClick: async () => {
              if (!rowIsSelected) {
                await navigateToTask();
              }
              appStore.openTaskDetailForTask(targetTask.duid);
              await nextTick();
              appStore.taskDetail?.openFilePicker();
              close?.();
            },
          },
          {
            title: "Set recurrence",
            icon: RecurrenceIcon,
            kind: DropdownMenuItemKind.COMPONENT,
            component: DatePicker,
            componentArgs: {
              value: (dataStore.getTaskByDuidIfLoaded(targetTask.duid) ?? targetTask).recursNextAt,
              titleOverride: "Set recurrence",
              iconOverride: RecurrenceIcon,
              isRecurrence: true,
              tasks: selectedTasks,
              editorMode: EditorMode.CONTEXT_MENU,
              recurrence: (dataStore.getTaskByDuidIfLoaded(targetTask.duid) ?? targetTask).recurrence,
              recursNextAt: (dataStore.getTaskByDuidIfLoaded(targetTask.duid) ?? targetTask).recursNextAt,
              placement: Placement.RIGHT_TOP,
            },
          },
          {
            title: "Set reminder",
            icon: ReminderIcon,
            kind: DropdownMenuItemKind.COMPONENT,
            component: DatePicker,
            componentArgs: {
              value: (dataStore.getTaskByDuidIfLoaded(targetTask.duid) ?? targetTask).remindAt,
              titleOverride: "Set reminder",
              iconOverride: ReminderIcon,
              isReminder: true,
              tasks: selectedTasks,
              editorMode: EditorMode.CONTEXT_MENU,
              remindAt: (dataStore.getTaskByDuidIfLoaded(targetTask.duid) ?? targetTask).remindAt,
              placement: Placement.RIGHT_TOP,
            },
          },
          {
            title: "Subscribe",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownToggleItem,
            componentArgs: {
              title: "Subscribe",
              icon: NotificationIcon,
              value: userIsSubscriber,
              onUpdate: (value: boolean) => {
                if (!value) {
                  dataStore.removeSubscribers(selectedTaskDuids, [userStore.duid]);
                } else {
                  dataStore.addSubscribers(selectedTaskDuids, [userStore.duid]);
                }
              },
            },
            noFocus: true,
          },
        ],
      },
      copySection,
      {
        title: "Meta",
        items: [
          {
            title: "Export as",
            kind: DropdownMenuItemKind.COMPONENT,
            disabled: isMultipleTasks,
            component: ExportDropdown,
            componentArgs: {
              task: targetTask,
            },
          },
          {
            title: "Replicate",
            kind: DropdownMenuItemKind.BUTTON,
            icon: ReplicateIcon,
            commandId: CommandId.REPLICATE_TASK,
            onClick: () => actions.visualization.replicateTasks(selectedTasks),
          },
          deleteItem,
        ],
      },
    ];
  },
  taskKind(taskKind: TaskKind, dialog: InstanceType<typeof ConfirmationDialog> | null) {
    const environmentStore = useEnvironmentStore();

    return [
      {
        title: "Options",
        items: [
          {
            title: "Copy settings link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => {
              const fullUrl = environmentStore.getTaskKindSettingsUrl(taskKind.duid);
              copyAndNotify("Link to type settings", fullUrl);
            },
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Type ID", taskKind.duid),
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownDeleteMenuItem,
            disabled: taskKind.locked,
            disabledReason: taskKind.locked ? "This is the default type and can't be deleted" : undefined,
            componentArgs: {
              dialog: dialog ?? undefined,
            },
          },
        ],
      },
    ];
  },
  user(
    user: User,
    emit: (e: string) => void,
    options: { hideDestructiveActions?: boolean; hideAgentAdmin?: boolean } = {}
  ) {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const pageStore = usePageStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    const router = dataStore.$router;
    const { hideDestructiveActions = false, hideAgentAdmin = false } = options;

    const allUsers = dataStore.getUserList({ excludeAgents: true, includeSpecialists: true });
    const activeUsers = allUsers.filter((e) => e.status === UserStatus.ACTIVE);
    const userIsAdmin = userStore.isRoleGte(UserRole.ADMIN);
    const isAgent = user.kind !== USER_KIND_USER;
    const removingLastActiveUser = user.status === UserStatus.ACTIVE && activeUsers.length <= 1;
    const leavingAsOnlyAdmin =
      user.duid === userStore.duid &&
      user.role === UserRole.ADMIN &&
      !activeUsers.some((e) => e.duid !== user.duid && e.role === UserRole.ADMIN);

    const { getUpgradeDialogTitle, handleUpgradeClick } = useUpgrade(tenantStore, Subscription.PREMIUM);

    const sections = [
      {
        title: "Actions",
        items: [
          {
            title: `Open ${isAgent ? "agent" : "user"} page`,
            hidden: !hideDestructiveActions && (!isAgent || router.currentRoute.value.name === "agent"),
            kind: DropdownMenuItemKind.BUTTON,
            icon: isAgent ? AgentIcon : AccountSettingsIcon,
            onClick: () => router.push(getUserPageLink(user, router.currentRoute.value)),
          },
        ],
      },
      {
        title: "Edit",
        items: [
          // User only items
          {
            title: getUpgradeDialogTitle(Subscription.PREMIUM),
            hidden: isAgent || user.status !== UserStatus.PENDING_SUBSCRIPTION_UPGRADE,
            kind: DropdownMenuItemKind.BUTTON,
            icon: UpgradeRequiredIcon,
            onClick: () => handleUpgradeClick(Subscription.PREMIUM),
          },
          {
            title: "Resend invitation email",
            hidden: isAgent || user.status !== UserStatus.INVITED,
            kind: DropdownMenuItemKind.BUTTON,
            icon: RepeatIcon,
            onClick: async () => {
              const { data } = await backendOld.workspace.reinviteUsers([user.duid]);
              notify({
                message: data.successCount > 0 ? "Invitation sent" : data.errors.flat().join(" "),
                type: data.successCount > 0 ? NotificationType.SUCCESS : NotificationType.ERROR,
              });
            },
          },
        ],
      },
      {
        title: "Copy",
        items: [
          {
            title: "Copy link by kind",
            hidden: !isAgent || user.kind === USER_KIND_CUSTOM || !tenantStore.isDart || pageStore.adminHidden,
            kind: DropdownMenuItemKind.BUTTON,
            icon: AdminIcon,
            onClick: () => copyAndNotify("Link to agent by kind", environmentStore.getAgentUrlByKind(user.kind)),
          },
          {
            title: "Copy link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => copyAndNotify(isAgent ? "Link to agent" : "Link to user", environmentStore.getUserUrl(user)),
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify(isAgent ? "Agent ID" : "User ID", user.duid),
          },
          {
            title: "Make login token",
            hidden: !tenantStore.isDart || pageStore.adminHidden || !userIsAdmin || isAgent,
            kind: DropdownMenuItemKind.BUTTON,
            icon: AdminIcon,
            onClick: async () => {
              const loginToken = (await backendOld.auth.makeTeammateLoginTokenAdmin(user.duid)).data;
              copyAndNotify("Login token", loginToken);
            },
          },
        ],
      },
      {
        title: "Manage",
        items: [
          // Agent only items
          {
            title: "Replicate",
            hidden:
              !isAgent ||
              user.kind === USER_KIND_DART_AI ||
              hideAgentAdmin ||
              (user.kind !== USER_KIND_CUSTOM && !AGENT_CONFIG_MAP.get(user.kind)),
            kind: DropdownMenuItemKind.BUTTON,
            icon: ReplicateIcon,
            onClick: () => createAndOpenAgent(AGENT_CONFIG_MAP.get(user.kind), user),
          },
          {
            title: "Enable",
            hidden: !isAgent || hideAgentAdmin || isAgentSetupRequired(user),
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownToggleItem,
            componentArgs: {
              title: "Enable",
              icon: ShowIcon,
              value: !isAgentDisabled(user),
              onUpdate: (value: boolean) => {
                dataStore.updateAgent({
                  duid: user.duid,
                  status: value ? UserStatus.ACTIVE : UserStatus.AGENT_DISABLED,
                });
                hideAllPoppers();
                appStore.contextMenu = null;
              },
            },
            noFocus: true,
          },
          {
            title: "Delete",
            hidden: !isAgent || user.kind === USER_KIND_DART_AI || hideDestructiveActions || hideAgentAdmin,
            kind: DropdownMenuItemKind.BUTTON,
            icon: UsersRemoveIcon,
            onClick: () =>
              appStore.setDeleteConfirmationModalOpen({
                title: `Delete ${user.name ?? "agent"}`,
                description: DELETE_AGENT_DIALOG_DESCRIPTION,
                confirmText: "Delete",
                cancelText: "Cancel",
                onDelete: () => dataStore.deleteAgent(user),
              }),
          },
          // User only items
          {
            title: user.duid === userStore.duid ? "Leave workspace" : "Remove from workspace",
            hidden: isAgent || (user.duid !== userStore.duid && !userIsAdmin) || hideDestructiveActions,
            disabled: removingLastActiveUser || leavingAsOnlyAdmin,
            disabledReason: removingLastActiveUser
              ? "You're the only person in this workspace"
              : "You're the only admin, make someone else an admin before leaving",
            kind: DropdownMenuItemKind.BUTTON,
            icon: user.duid === userStore.duid ? LogOutIcon : UsersRemoveIcon,
            dataTestid: "remove-user",
            onClick: () => emit("removeUser"),
          },
        ],
      },
    ];

    return sections;
  },
});
